import { useCallback, type WheelEvent } from 'react';
import { clampPan, type ContentBounds } from './clampPan.tsx';
import { getZoomFromDeltaY } from './getZoomFromDeltaY.tsx';
import type { ViewBoxRect } from './ListEdgeDetails.tsx';
import { add } from './percentageOfLine.tsx';
import { type Point, toPoint } from './toPortInfo.tsx';
import { useLatest } from './useLatest.tsx';

export interface Options {
	pan: Point;
	zoom: number;
	updateZoom: (zoom: number) => void;
	updatePan: (pan: Point) => void;
	minZoom?: number;
	contentBounds?: ContentBounds;
	viewBox?: ViewBoxRect;
}

export const useOnWheel = ({
	pan,
	zoom,
	updateZoom,
	updatePan,
	minZoom,
	contentBounds,
	viewBox,
}: Options): ((e: WheelEvent) => void) => {
	const latestPan = useLatest(pan);
	const latestContentBounds = useLatest(contentBounds);
	const latestViewBox = useLatest(viewBox);

	return useCallback(
		(e: WheelEvent) => {
			e.preventDefault();
			// Mac style zoom, two finger = pan, pinch to zoom
			const shouldZoom = e.ctrlKey;
			const shouldPan = !shouldZoom;

			if (shouldZoom) {
				updateZoom(getZoomFromDeltaY(e.deltaY, zoom, minZoom));
			} else if (shouldPan) {
				if (!e.target) {
					return;
				}

				const rawPan = add(latestPan.current, toPoint(e.deltaX, e.deltaY));
				const newPan = latestViewBox.current
					? clampPan(rawPan, latestContentBounds.current, latestViewBox.current)
					: rawPan;
				updatePan(newPan);
			}
		},
		[latestPan, latestContentBounds, latestViewBox, updatePan, updateZoom, zoom, minZoom],
	);
};

export default useOnWheel;
